import React, { useState } from 'react';
import { useAppSelector, useAppDispatch } from '../../store/hooks';
import { loginSuccess } from '../../store/slices/authslice';
import axios from 'axios';
import { endpoints } from '../../api/api';

const Profile = () => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);

  const initialFirst = user?.firstName || user?.name?.split(' ')[0] || '';
  const initialLast = user?.lastName || user?.name?.split(' ').slice(1).join(' ') || '';

  const [isEditing, setIsEditing] = useState(false);
  const [firstName, setFirstName] = useState(initialFirst);
  const [lastName, setLastName] = useState(initialLast);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleEdit = () => {
    setFirstName(user?.firstName || user?.name?.split(' ')[0] || '');
    setLastName(user?.lastName || user?.name?.split(' ').slice(1).join(' ') || '');
    setError(null);
    setSuccess(false);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setError(null);
  };

  const handleSave = async () => {
    const trimmedFirst = firstName.trim();
    const trimmedLast = lastName.trim();

    if (!trimmedFirst) {
      setError('First name is required.');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      const response = await axios.put(endpoints.updateProfile, {
        firstName: trimmedFirst,
        lastName: trimmedLast,
      });

      const updatedUser = response.data.user;
      const newName = `${trimmedFirst} ${trimmedLast}`.trim();

      // Persist to localStorage
      const stored = JSON.parse(localStorage.getItem('user') || '{}');
      const merged = {
        ...stored,
        firstName: trimmedFirst,
        lastName: trimmedLast,
        name: updatedUser?.name || newName,
      };
      localStorage.setItem('user', JSON.stringify(merged));

      // Update Redux
      dispatch(loginSuccess({
        ...user,
        firstName: trimmedFirst,
        lastName: trimmedLast,
        name: updatedUser?.name || newName,
      }));

      setSuccess(true);
      setIsEditing(false);
    } catch (err) {
      setError(err?.data?.message || err?.response?.data?.message || err?.message || 'Failed to update profile.');
    } finally {
      setIsSaving(false);
    }
  };

  const displayFirst = user?.firstName || user?.name?.split(' ')[0] || 'N/A';
  const displayLast = user?.lastName || user?.name?.split(' ').slice(1).join(' ') || '';

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="px-4 md:px-6 py-8 max-w-3xl mx-auto">
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          {/* Profile Header */}
          <div className="bg-gradient-to-r from-gray-900 to-gray-700 px-6 py-8">
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center">
                <span className="text-gray-900 text-3xl font-bold">
                  {user?.name?.charAt(0).toUpperCase() || 'U'}
                </span>
              </div>
              <div>
                <h1 className="text-white text-2xl md:text-3xl font-bold mb-1">
                  {user?.name || 'User'}
                </h1>
                <p className="text-gray-300 text-sm">User Profile</p>
              </div>
            </div>
          </div>

          {/* Profile Details */}
          <div className="p-6 md:p-8">
            <h2 className="text-gray-900 text-xl font-semibold mb-6">Personal Information</h2>

            {success && (
              <p className="text-green-600 text-sm mb-4">Profile updated successfully.</p>
            )}
            {error && !isEditing && (
              <p className="text-red-600 text-sm mb-4">{error}</p>
            )}

            <div className="space-y-5">
              {/* First Name */}
              <div className="border-b border-gray-100 pb-4">
                <label className="text-gray-600 text-sm font-medium block mb-1">First Name</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => { setFirstName(e.target.value); setError(null); }}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-gray-900 text-sm focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500"
                    placeholder="First name"
                    autoFocus
                  />
                ) : (
                  <p className="text-gray-900 text-base">{displayFirst}</p>
                )}
              </div>

              {/* Last Name */}
              <div className="border-b border-gray-100 pb-4">
                <label className="text-gray-600 text-sm font-medium block mb-1">Last Name</label>
                {isEditing ? (
                  <>
                    <input
                      type="text"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-gray-900 text-sm focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500"
                      placeholder="Last name"
                    />
                    {error && <p className="text-red-600 text-sm mt-1">{error}</p>}
                  </>
                ) : (
                  <p className="text-gray-900 text-base">{displayLast || 'N/A'}</p>
                )}
              </div>

              {/* Email */}
              <div className="border-b border-gray-100 pb-4">
                <label className="text-gray-600 text-sm font-medium block mb-1">Email Address</label>
                <p className="text-gray-900 text-base">{user?.email || 'Not provided'}</p>
              </div>

              {/* Phone Number */}
              <div className="pb-4">
                <label className="text-gray-600 text-sm font-medium block mb-1">Phone Number</label>
                <p className="text-gray-900 text-base">{user?.mobile || 'Not provided'}</p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="mt-8 pt-6 border-t border-gray-200 flex gap-3">
              {isEditing ? (
                <>
                  <button
                    onClick={handleSave}
                    disabled={isSaving}
                    className="bg-orange-500 hover:bg-orange-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white px-6 py-2.5 rounded-lg font-medium transition-colors text-sm"
                  >
                    {isSaving ? 'Saving...' : 'Save Changes'}
                  </button>
                  <button
                    onClick={handleCancel}
                    disabled={isSaving}
                    className="bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 px-6 py-2.5 rounded-lg font-medium transition-colors text-sm"
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <button
                  onClick={handleEdit}
                  className="bg-gray-900 text-white px-6 py-2.5 rounded-lg hover:bg-gray-800 font-medium transition-colors text-sm"
                >
                  Edit Profile
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
